/**
 * The store's colour on the admin, when the owner chose it (ADR-123).
 *
 * `GET /admin/workspace` sends the store's brand ramp and hue as CSS variables;
 * they go into one unlayered `:root` block at the end of `<head>`. That outranks
 * the admin's own light values (Tailwind's theme layer) and loses to the dark
 * blocks, which climb up whichever ramp is in force — so dark mode keeps working.
 *
 * The last accent is remembered per browser and put back before the first paint,
 * so a reload does not flash the platform's emerald before the workspace answers.
 * It is a convenience: the workspace is the truth, and replaces it on every load.
 */

const STYLE_ID = 'store-accent';
const STORAGE_KEY = 'ecom_admin_accent';

/** Only custom properties, and values that cannot close the block they sit in. */
function declarations(tokens: Record<string, string>): string {
  return Object.entries(tokens)
    .filter(([name, value]) => /^--[a-z0-9-]+$/.test(name) && typeof value === 'string' && !/[;{}<>]/.test(value))
    .map(([name, value]) => `${name}:${value}`)
    .join(';');
}

export function applyAccent(tokens: Record<string, string> | null): void {
  const existing = document.getElementById(STYLE_ID);
  const body = tokens ? declarations(tokens) : '';

  if (!body) {
    existing?.remove();
    remember(null);
    return;
  }

  const style = existing ?? Object.assign(document.createElement('style'), { id: STYLE_ID });
  style.textContent = `:root{${body}}`;
  // Last in <head>, after the stylesheets it has to outrank.
  document.head.appendChild(style);
  remember(tokens);
}

/** The accent this browser saw last, before anything has been fetched. */
export function restoreAccent(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) applyAccent(JSON.parse(raw) as Record<string, string>);
  } catch {
    // Storage blocked or the value unreadable: the platform palette it is.
  }
}

function remember(tokens: Record<string, string> | null): void {
  try {
    if (tokens) localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Remembering is a nicety; the workspace re-applies it on every load.
  }
}
